import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import { createOtherCostsGroupRelatives } from "@aprao/model/src/stores/createOtherCostsGroupRelatives.js";
import type { OtherCostsRelativesStore } from "@aprao/model/src/stores/createOtherCostsRelatives.js";
import {
  type OtherCostsGroupFacade,
  withCostsPredicate,
} from "@aprao/model/src/stores/OtherCostsGroupFacade.js";
import { withAbsoluteValuePredicate } from "@aprao/model/src/stores/RelativesSetFacade.js";

import { useI18n } from "../../common/ApraoI18n.js";
import { useNumberUnits } from "../../common/useNumberUnits.js";
import { buildOtherCostsGroupName } from "../../i18n/buildOtherCostsGroupName.js";
import { Section } from "../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../SimpleTableRow.js";

const Group = (props: {
  appraisal: AppraisalDocumentFacade;
  group: OtherCostsGroupFacade;
}) => {
  const { appraisal, group } = props;
  const otherCostsGroupRelatives = createOtherCostsGroupRelatives(
    group,
    appraisal,
  );

  const { msg } = useI18n();
  const { currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          buildOtherCostsGroupName(
            group.$model,
            appraisal.$model.stages.otherCosts.otherCostsGroups,
          ),
          null,
          null,
          msg("global.percentBuild"),
          msg("global.percentGdv"),
          `Total (${currency.symbol})`,
        ]}
      />
      {group.costs
        .filter(({ relativesSet }) => withAbsoluteValuePredicate(relativesSet))
        .map((cost, idxCost) => (
          <SimpleTableRow
            key={idxCost}
            cells={[
              cost.named.name,
              null,
              null,
              formatPercentage(
                cost.relativesSet.getRelative("ratioBuildCostGlobalTotal") ??
                  FRACTION_ZERO,
              ),
              formatPercentage(
                cost.relativesSet.getRelative("ratioGdv") ?? FRACTION_ZERO,
              ),
              formatDecimalNumber(
                cost.relativesSet.getRelative("absolute") ?? FRACTION_ZERO,
              ),
            ]}
          />
        ))}
      <SimpleTableRow
        bold
        borderTop
        borderBottom
        cells={[
          "Total",
          null,
          null,
          formatPercentage(
            otherCostsGroupRelatives.relativesSet.getRelative(
              "ratioBuildCostGlobalTotal",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            otherCostsGroupRelatives.relativesSet.getRelative("ratioGdv") ??
              FRACTION_ZERO,
          ),
          formatDecimalNumber(group.costsTotal ?? FRACTION_ZERO),
        ]}
      />
    </SimpleTableSection>
  );
};

export const OtherCosts = (props: {
  appraisal: AppraisalDocumentFacade;
  otherCostsRelatives: OtherCostsRelativesStore;
}) => {
  const { appraisal, otherCostsRelatives } = props;

  const { msg } = useI18n();

  return (
    <Section title={msg("global.other")}>
      {appraisal.otherCostsGroups
        .filter(withCostsPredicate)
        .map((group, idxGroup) => (
          <Group appraisal={appraisal} group={group} key={idxGroup} />
        ))}
      <SimpleTableSection>
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          thickerBorder
          cells={[
            msg("global.totalOtherCosts"),
            null,
            null,
            formatPercentage(
              otherCostsRelatives.relativesSet.getRelative(
                "ratioBuildCostGlobalTotal",
              ) ?? FRACTION_ZERO,
            ),
            formatPercentage(
              otherCostsRelatives.relativesSet.getRelative("ratioGdv") ??
                FRACTION_ZERO,
            ),
            formatDecimalNumber(appraisal.otherCostsTotal ?? FRACTION_ZERO),
          ]}
        />
      </SimpleTableSection>
    </Section>
  );
};
